import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { isEqual } from 'lodash';
import {
  NotesContext,
  useNotesContext,
} from 'components/Charts/ScheduledActivities/notesContext';
import { ContentWidth } from '__components/Layout/PageContentWidth';
import Loader from 'components/Kizen/Loader';
import Input from '__components/Kizen/Input';
import { TRow } from 'components/Kizen/Table';
import ActivityService from 'services/ActivityService';
import CustomObjectsService from 'services/CustomObjectsService';
import useModal from 'components/Modals/useModal';
import { columnSize, SCHEDULED_ACTIVITIES_ACTIONS } from './Table';
import {
  useDefaultActivityRelationshipsPrefetch,
  useDefaultActivityRelationshipsQuery,
} from 'queries/models/activites';
import {
  LOG_ACTIVITY_SUCCESS,
  SCHEDULE_ACTIVITY_FAIL,
  SCHEDULE_ACTIVITY_LOAD_FAIL,
  SCHEDULE_ACTIVITY_SUCCESS,
} from 'pages/Common/activityHelpers';
import { toastVariant, useToast } from 'components/ToastProvider';
import { parseMentions } from 'components/Inputs/NotesRichTextEditor';
import { useMutation, useQueries, useQueryClient } from '@tanstack/react-query';
import { CUSTOM_OBJECTS } from 'queries/query-keys';
import { EMPTY_OBJECT } from 'utility/fieldHelpers';
import { getBusinessTimeZone } from 'store/authentication/selectors';
import { getOriginalError } from 'services/AxiosService';
import { useVirtualizer } from '@tanstack/react-virtual';
import { GradientRightRow } from 'components/Kizen/LazyLoad/helpers';
import {
  CONFIRMATION_TYPES,
  DEFAULT_PAGE_SIZE,
  INVALID_SORT_ERROR_MESSAGE,
  MIN_COLUMN_WIDTH,
} from '__components/Charts/ScheduledActivities/constants';
import {
  makeDefaultAssignment,
  mapContactColumn,
  mapFetchUrls,
  mapRelatedColumns,
} from '__components/Charts/ScheduledActivities/helpers';
import CollapsibleRow from '__components/Charts/ScheduledActivities/CollapsibleRow';
import NotesPanel from '__components/Charts/ScheduledActivities/NotesPanel';
import TablePagination from '__components/Tables/Big/TablePagination';
import { PageSizing } from '__pages/FormSettings/pages/styles';
import KizenTypography from '__app/kizentypo';
import {
  PaginationWrapper,
  StyledBigTableLayout,
  StyledLazyLoadTable,
  TBSearchInput,
  Toolbar,
} from '__pages/ActivitySettings/pages/ScheduledAndLogged/styled';
import { EditColumnsModal } from '__pages/ActivitySettings/pages/ScheduledAndLogged/modals/EditColumnsModal';
import { useDebounce } from 'react-use';
import { DEFAULT_INPUT_DELAY } from 'utility/config';
import { useFetchActivities } from '__pages/ActivitySettings/pages/ScheduledAndLogged/hooks/useFetchActivities';
import { useConfig } from '__pages/ActivitySettings/pages/ScheduledAndLogged/hooks/useConfig';
import ConfirmDeletionModal from '__components/Modals/presets/ConfirmDeletion';
import { CompleteActivityModal } from '__components/Modals/ActivityCompletionModal/ActivityCompletionModal';
import {
  ConfirmNoteEdits,
  ConfirmNoteEditsWithMentions,
  ScheduleActivityModal,
} from '__components/Charts/ScheduledActivities/ScheduleActivityModal';
import { ConfirmationModal } from 'components/Charts/ScheduledActivities/ConfirmationModal';
import { useModalControl } from '__hooks/useModalControl';

export const ActivitiesTable = ({
  activityObjectId,
  configPropKey,
  columnsConfigPropKey,
  getQueryKey,
  getInvalidateQueryKey,
  fetchActivitiesFunc,
  createColumnsFunc,
  defaultColumnSettings,
  defaultPageSettings,
  title,
  canEdit,
  showGroupedColumnEditor,
}) => {
  const { t } = useTranslation();

  const [showToast] = useToast();
  const queryClient = useQueryClient();
  // Makes the relationship data ready to go when needed
  useDefaultActivityRelationshipsPrefetch();
  const [showDeletionModal, setShowDeletionModal] = useState(false);
  const [completeActivity, setCompleteActivity] = useState(null);
  const [loggingActivity, setLoggingActivity] = useState(false);
  const [showNotesId, setShowNotesId] = useState(null);
  const [editActivity, setEditActivity] = useState(null);

  const notesValue = useNotesContext();
  const [editorKey, setEditorKey] = useState(() => Date.now());
  const [isSchedulingActivity, setSchedulingActivity] = useState(false);
  const [disableAutoOpen, setDisableAutoOpen] = useState(true);
  const [fetchActivityError, setFetchActivityError] = useState('');
  const [observedWidth, setObservedWidth] = useState(null);
  const wrapperRef = useRef(null);

  const confirmationPropsRef = useRef({});

  const businessTimezone = useSelector(getBusinessTimeZone);
  const access = useSelector((s) => s.authentication.access);
  const contactAccess = access?.sections?.contacts_section?.view ?? false;

  const [columnWidths, setColumnWidths] = useState({});
  // page settings
  const [sortOrder, setSortOrder] = useState(null);
  const [pageSize, setPageSize] = useState(null);
  const [page, setPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState(null);
  const [debouncedTerm, setDebouncedTerm] = useState(searchTerm);

  // end page settings
  const [isConfirmActivityModalOpen, { hideModal: hideConfirmActivityModal }] =
    useModalControl();

  const [
    isConfirmModalOpen,
    { showModal: showConfirmationModal, hideModal: hideConfirmationModal },
  ] = useModalControl();

  const defaultActivityRelationshipsQuery =
    useDefaultActivityRelationshipsQuery();

  const {
    fetchUrlList = EMPTY_OBJECT,
    contactSortBy = { clientVal: 'client' },
    relatedObjects = null,
  } = useMemo(() => {
    if (defaultActivityRelationshipsQuery.isLoading) {
      return {};
    }
    const doa = defaultActivityRelationshipsQuery.data;
    return {
      fetchUrlList: mapFetchUrls(doa),
      contactSortBy: mapContactColumn(doa),
      relatedObjects: mapRelatedColumns(doa),
    };
  }, [
    defaultActivityRelationshipsQuery.data,
    defaultActivityRelationshipsQuery.isLoading,
  ]);

  const {
    columnSettings,
    pageSettings,
    isPageConfigLoading,
    updateColumnsConfig,
    updatePageConfig,
  } = useConfig({
    configPropKey,
    columnsConfigPropKey,
    defaultColumnSettings,
    activityObjectId,
  });

  const searchChanged = searchTerm !== debouncedTerm;

  useDebounce(
    () => {
      if (searchChanged) {
        setDebouncedTerm(searchTerm);
        setPage(1);
        updatePageConfig({
          search: searchTerm,
        });
      }
    },
    DEFAULT_INPUT_DELAY,
    [searchTerm, searchChanged]
  );

  const savedColumns = useMemo(() => {
    return columnSettings?.columns?.length > 0
      ? columnSettings?.columns
      : defaultColumnSettings.columns;
  }, [columnSettings?.columns, defaultColumnSettings.columns]);

  const {
    fields,
    isLoadingFields,
    query: { error, isFetching: fetching },
    queryKey,
    invalidateQueryKey,
    activities,
    activityCount,
  } = useFetchActivities({
    activityObjectId,
    page,
    pageSize,
    sortOrder,
    searchTerm: debouncedTerm,
    debouncedTerm,
    fetchActivitiesFunc,
    getQueryKey,
    getInvalidateQueryKey,
    notesValue,
    isPageConfigLoading,
    showGroupedColumnEditor,
    savedColumns,
  });

  const [
    isEditColumnsModalOpen,
    { showModal: showEditColumnsModal, hideModal: hideEditColumnsModal },
  ] = useModalControl();

  const errorValues = Object.values(error?.data.errors ?? {});
  const invalidSortError = errorValues?.some(
    (error) =>
      typeof error === 'string' && error?.includes(INVALID_SORT_ERROR_MESSAGE)
  );

  if (invalidSortError) {
    setSortOrder({
      column: defaultColumnSettings.sort.column,
      direction: 'asc',
    });
    updateColumnsConfig({
      columns: savedColumns,
      sort: {
        column: defaultColumnSettings.sort.column,
        direction: 'asc',
      },
    });
  }
  if (fetchActivityError === '' && errorValues.length > 0) {
    setFetchActivityError(errorValues[0]);
  }

  const currentActivity = useMemo(() => {
    return showNotesId
      ? activities?.find(({ id }) => id === showNotesId) ?? null
      : null;
  }, [showNotesId, activities]);
  const [currentNote, setCurrentNote] = useState(currentActivity?.note);

  if (activities && activities.length) {
    if (!showNotesId && !disableAutoOpen) {
      setShowNotesId(activities[0].id);
      notesValue.setCurrentId(activities[0].id);
      notesValue.setIsDirty(false);
      notesValue.setOriginalNote(activities[0].note);
    }
  }

  const { mutate: mutateActivity, isLoading: updateActivityLoading } =
    useMutation({
      mutationFn: async (activity) =>
        await ActivityService.v2UpdateScheduledActivities(activity),
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: invalidateQueryKey,
        });
      },
      onError: ({ payload }) => {
        showToast({
          message:
            payload?.response?.data?.message ||
            t(
              'The Activity Notes were not saved. Please try again or contact Kizen support.'
            ),
          variant: toastVariant.FAILURE,
        });
      },
    });

  const { mutate: deleteActivity } = useMutation({
    mutationFn: async () =>
      await ActivityService.v2DeleteScheduledActivities(showDeletionModal),
    onError: ({ payload }) => {
      showToast({
        message: payload?.response?.data?.message || SCHEDULE_ACTIVITY_FAIL(t),
        variant: toastVariant.FAILURE,
      });
    },
    onMutate: async () => {
      notesValue.reset();
    },
    onSuccess: () => {
      showToast({
        message: SCHEDULE_ACTIVITY_SUCCESS(t),
        variant: toastVariant.SUCCESS,
      });
      setShowDeletionModal(false);
      queryClient.invalidateQueries({
        queryKey: invalidateQueryKey,
      });
    },
  });

  const { mutateAsync: logActivity } = useMutation({
    mutationFn: async ({ activityObjectId, payload }) => {
      return await ActivityService.v2CompleteActivity({
        activityObjectId,
        payload,
      });
    },
    onSuccess: () => {
      showToast({
        message: LOG_ACTIVITY_SUCCESS(t),
        variant: toastVariant.SUCCESS,
      });
      setCompleteActivity(null);
      setLoggingActivity(false);
      queryClient.invalidateQueries({
        queryKey: invalidateQueryKey,
      });
    },
  });

  const { mutate: createScheduleActivity } = useMutation({
    mutationFn: async (activityInfo) =>
      await ActivityService.v2CreateScheduledActivities(activityInfo),
    onSuccess: () => {
      showToast({
        message: SCHEDULE_ACTIVITY_SUCCESS(t),
        variant: toastVariant.SUCCESS,
      });
      setSchedulingActivity(false);
      setEditorKey(Date.now());
      queryClient.invalidateQueries({
        queryKey: invalidateQueryKey,
      });
    },
  });

  const { mutate: updateScheduleActivity } = useMutation({
    mutationFn: async (activityInfo, opts) =>
      await ActivityService.v2UpdateScheduledActivities(activityInfo, opts),
    onSuccess: () => {
      showToast({
        message: SCHEDULE_ACTIVITY_SUCCESS(t),
        variant: toastVariant.SUCCESS,
      });
      setSchedulingActivity(false);
      setEditorKey(Date.now());
      queryClient.invalidateQueries({
        queryKey: invalidateQueryKey,
      });
    },
  });

  const handleLogActivityError = (error) => {
    const orig = getOriginalError(error);
    showToast({
      message: orig?.message || SCHEDULE_ACTIVITY_LOAD_FAIL(t),
      variant: toastVariant.FAILURE,
    });
    setLoggingActivity(false);
  };

  const [completeModalProps, , completeModal] = useModal({
    handleSubmit: logActivity,
    handleError: handleLogActivityError,
  });

  const [addModalProps, , addModal] = useModal({
    handleSubmit: async (activityInfo, opts = {}) => {
      if (activityInfo.id) {
        updateScheduleActivity(activityInfo, opts);
      } else {
        createScheduleActivity(activityInfo, opts);
      }
    },
    handleHide: () => {
      setEditActivity(null);
    },
    handleError: (error) => {
      const orig = getOriginalError(error);
      if (orig?.errors?.activity_object_id) {
        showToast({
          message: t('Scheduled activity not found (completed or deleted).'),
          variant: toastVariant.FAILURE,
        });
      } else {
        showToast({
          message: orig.message || SCHEDULE_ACTIVITY_LOAD_FAIL(t),
          variant: toastVariant.FAILURE,
        });
      }
      setSchedulingActivity(false);
    },
  });

  const showAddModal = addModal.show;

  const savedColumnIdsRef = useRef(savedColumns.map(({ id }) => id));

  const handleCancelNoteEdit = useCallback(() => {
    setCurrentNote(notesValue.originalNote);

    // notes are not dirty
    notesValue.setIsDirty(false);
    const { nextShowNotesId, isNotes } = confirmationPropsRef.current;

    // if it was
    if (isNotes) {
      notesValue.setOriginalNote(null);
      notesValue.setCurrentId(nextShowNotesId);
      setShowNotesId(nextShowNotesId);
    }

    hideConfirmationModal();
  }, [hideConfirmationModal, notesValue]);

  const showEditModal = useCallback(
    (activity, note) => {
      setEditActivity({
        ...activity,
        note:
          activity.id === currentActivity?.id && note ? note : activity.note,
        mentions: parseMentions(activity.note).map((m) => ({
          id: m,
        })),
      });
      showAddModal();
    },
    [currentActivity, showAddModal]
  );

  const showCompleteModal = useCallback(
    (activity, note) => {
      setDisableAutoOpen(true);
      const defaultAssignment = makeDefaultAssignment(
        activity.associatedEntities,
        fetchUrlList
      );
      setCompleteActivity({
        activity: {
          ...activity,
          note:
            activity.id === currentActivity?.id && note ? note : activity.note,
        },
        predefinedOptions: { defaultAssignment },
      });
      completeModal.show();
    },
    [completeModal, fetchUrlList, currentActivity]
  );

  const handleSelectAction = useCallback(
    (option, activity) => {
      const nextShowNotesId = activity.id === showNotesId ? null : activity.id;

      const handleShowConfirmationModal = (onContinue) => {
        const hasMentions = parseMentions(currentNote).length > 0;
        showConfirmationModal();
        confirmationPropsRef.current = {
          isNotes: true,
          nextShowNotesId,
          type: hasMentions
            ? CONFIRMATION_TYPES.SAVE_NOTIFY
            : CONFIRMATION_TYPES.SAVE,
          onContinue,
        };
      };

      switch (option.value) {
        case SCHEDULED_ACTIVITIES_ACTIONS.Complete.value: {
          if (notesValue.isDirty) {
            handleShowConfirmationModal((note) =>
              showCompleteModal(activity, note)
            );
          } else {
            showCompleteModal(activity);
          }
          break;
        }
        case SCHEDULED_ACTIVITIES_ACTIONS.ViewNotes.value:
        case SCHEDULED_ACTIVITIES_ACTIONS.HideNotes.value: {
          setDisableAutoOpen(true);
          if (showNotesId === null) {
            // opening with none open
            notesValue.setCurrentId(activity.id);
            notesValue.setIsDirty(false);
            notesValue.setOriginalNote(activity.note);
            setShowNotesId(nextShowNotesId);
          } else {
            // opening with another open or closing, both need to check dirty
            if (notesValue.isDirty) {
              handleShowConfirmationModal();
            } else {
              notesValue.setIsDirty(false);
              notesValue.setOriginalNote(
                nextShowNotesId ? activity.note : null
              );
              notesValue.setCurrentId(nextShowNotesId);
              setShowNotesId(nextShowNotesId);
            }
          }

          break;
        }
        case SCHEDULED_ACTIVITIES_ACTIONS.Edit.value: {
          if (notesValue.isDirty) {
            handleShowConfirmationModal((note) =>
              showEditModal(activity, note)
            );
          } else {
            showEditModal(activity);
          }
          break;
        }
        default: {
          // delete option
          setShowDeletionModal(activity.id);
        }
      }
    },
    [
      showNotesId,
      showEditModal,
      currentNote,
      notesValue,
      showConfirmationModal,
      showCompleteModal,
    ]
  );

  const columnAssociatedObjectFieldIds = useMemo(() => {
    return savedColumns
      .filter(({ fieldId }) => fieldId)
      .map(({ fieldId, objectId }) => ({
        fieldId,
        objectId,
      }));
  }, [savedColumns]);

  const objectQueries = useQueries({
    queries: columnAssociatedObjectFieldIds.map(({ objectId }) => {
      return {
        queryKey: CUSTOM_OBJECTS.DETAILS(objectId),
        queryFn: () => CustomObjectsService.getCustomObjectDetails(objectId),
        enabled: Boolean(objectId),
      };
    }),
  });

  const isCustomObjectsLoading = objectQueries.some((q) => q.isLoading);

  const objectsById = useMemo(() => {
    const result = {};
    for (const query of objectQueries) {
      if (query.data) {
        result[query.data.id] = {
          ...query.data,
          fieldsById: query.data.fields.reduce((acc, field) => {
            acc[field.id] = field;
            return acc;
          }, {}),
        };
      }
    }
    return result;
  }, [objectQueries]);

  const filteredColumnSettings = useMemo(() => {
    const activeColumns = savedColumns.reduce((acc, column) => {
      const { field_id, object_id, visible } = column;
      if (
        !field_id ||
        objectsById?.[object_id]?.fieldsById?.[field_id] ||
        !visible
      ) {
        acc.push({
          ...column,
          width: columnWidths[column.id],
        });
      }
      return acc;
    }, []);

    return {
      columns: activeColumns,
      sort: sortOrder,
    };
  }, [savedColumns, sortOrder, objectsById, columnWidths]);

  const columns = useMemo(() => {
    const getColumnWidth = (id) => {
      if (id) {
        const configuredMinWidth = columnSize(id);
        if (configuredMinWidth?.minWidth) {
          return parseFloat(columnWidths[id]) >
            parseFloat(configuredMinWidth?.minWidth)
            ? columnWidths[id]
            : `${parseFloat(configuredMinWidth?.minWidth)}px`;
        } else {
          return parseFloat(columnWidths[id]) > parseFloat(MIN_COLUMN_WIDTH)
            ? columnWidths[id]
            : MIN_COLUMN_WIDTH;
        }
      }
      return MIN_COLUMN_WIDTH;
    };

    const handleResizeColumnWidth = ({ id, width: newWidth }) => {
      if (id) {
        const newColumnWidths = {
          ...columnWidths,
          [id]: newWidth,
        };
        setColumnWidths(newColumnWidths);
      }
    };

    return createColumnsFunc(
      {
        showNotesId,
        onSelectAction: handleSelectAction,
        onUpdateActivity: async (activity, patch) => {
          mutateActivity({ id: activity.id, ...patch });
        },
        columnSettings: filteredColumnSettings,
        objectsById,
        onResizeColumnWidth: handleResizeColumnWidth,
        onResizeStop: () => {
          updateColumnsConfig({
            ...filteredColumnSettings,
            sort: filteredColumnSettings.sort,
          });
        },
        getColumnWidth,
        contactSortBy,
        timezone: businessTimezone,
        virtualized: true,
        canEdit,
        showColumnEditorIcon: true,
        fields,
        relatedObjects,
      },
      t
    );
  }, [
    filteredColumnSettings,
    objectsById,
    mutateActivity,
    contactSortBy,
    businessTimezone,
    handleSelectAction,
    showNotesId,
    updateColumnsConfig,
    canEdit,
    columnWidths,
    fields,
    relatedObjects,
    t,
    createColumnsFunc,
  ]);

  const headData = useMemo(
    () => ({
      meta: {
        sort: filteredColumnSettings.sort,
        onSort: async (column, direction) => {
          setSortOrder({
            column,
            direction,
          });
          await updateColumnsConfig({
            ...filteredColumnSettings,
            sort: { column, direction },
          });
        },
        onClickColumns: showEditColumnsModal,
      },
    }),
    [filteredColumnSettings, updateColumnsConfig, showEditColumnsModal]
  );

  const handleUpdateNote = useCallback(
    ({ notifyMentioned = false } = {}) => {
      mutateActivity({
        id: currentActivity.id,
        note: currentNote,
        mentions: parseMentions(currentNote),
        notifyMentioned,
      });
      notesValue.setIsDirty(false);
      const { nextShowNotesId } = confirmationPropsRef.current;
      notesValue.setOriginalNote(null);
      notesValue.setCurrentId(nextShowNotesId);
      setShowNotesId(nextShowNotesId);
    },
    [currentNote, currentActivity, notesValue, mutateActivity]
  );

  const handleSubmitContactData = async () => {
    await hideConfirmActivityModal();
    setTimeout(() => {
      completeModal.show();
      // TOAST_DELAY
    }, 1500);
  };

  const handleDiscardChanges = async () => {
    hideConfirmActivityModal();
    completeModal.show();
  };

  const tableBodyRef = useRef(null);
  const virtualScrollerRef = useRef(null);

  const activitiesWithHoles = useMemo(() => {
    const result = [];
    if (activities) {
      for (const activity of activities) {
        result.push(activity, null);
      }
    }
    return result;
  }, [activities]);

  const desktopShowNotesIndex = useMemo(() => {
    return (
      (activitiesWithHoles?.findIndex((elem) => elem?.id === showNotesId) ??
        -2) + 1
    );
  }, [activitiesWithHoles, showNotesId]);

  const mobileShowNotesIndex = useMemo(() => {
    return activities?.findIndex((elem) => elem?.id === showNotesId) ?? -1;
  }, [activities, showNotesId]);

  const estimateDesktopSizeFromIndex = (index) => {
    if (index % 2 === 0) {
      return 40; // Height of the activity row
    }

    if (index === desktopShowNotesIndex) {
      return 216; // Height of the notes row when expanded
    }

    return 0; // Collapsed notes row
  };

  const desktopVirtualizer = useVirtualizer({
    count: (activities?.length ?? 0) * 2,
    estimateSize: estimateDesktopSizeFromIndex,
    getScrollElement: () => virtualScrollerRef.current,
    overscan: 7,
  });

  const loading = isPageConfigLoading || isCustomObjectsLoading;

  const totalSize = desktopVirtualizer.getTotalSize();

  const setTableStyles = useCallback(() => {
    tableBodyRef.current?.setAttribute(
      'style',
      `height: ${totalSize}px; width: 100%; position: relative;`
    );
  }, [totalSize]);

  const observer = useMemo(() => {
    return new ResizeObserver((entries) => {
      if (entries[0]) {
        setObservedWidth(entries[0].contentRect.width);
      }
    });
  }, []);

  useEffect(() => {
    if (!loading) {
      const columnWidthMap = (columnSettings?.columns || []).reduce(
        (acc, column) => {
          acc[column.id] = column.width;
          return acc;
        },
        {}
      );
      setColumnWidths(columnWidthMap);
      setSortOrder(columnSettings?.sort ?? defaultColumnSettings.sort);
      setPageSize(pageSettings?.page_size ?? defaultPageSettings.page_size);
      setSearchTerm(pageSettings?.search ?? defaultPageSettings.search);
      setDebouncedTerm(pageSettings?.search ?? defaultPageSettings.search);
    }
  }, [
    defaultPageSettings,
    defaultColumnSettings,
    columnSettings,
    setColumnWidths,
    pageSettings,
    setSearchTerm,
    loading,
  ]);

  useEffect(() => {
    const newColumnIds = savedColumns.map(({ id }) => id);
    if (!isEqual(savedColumnIdsRef.current, newColumnIds)) {
      savedColumnIdsRef.current = newColumnIds;
      queryClient.invalidateQueries({
        queryKey: invalidateQueryKey,
      });
    }
  }, [queryClient, savedColumns, invalidateQueryKey, activityObjectId]);

  useEffect(() => {
    desktopVirtualizer.measure();
  }, [desktopShowNotesIndex, mobileShowNotesIndex, desktopVirtualizer]);

  useEffect(() => {
    const currentElement = wrapperRef.current;
    if (!loading && currentElement) {
      observer.observe(currentElement);
    }

    return () => {
      if (currentElement) {
        observer.unobserve(currentElement);
      }
    };
  }, [observer, wrapperRef, setObservedWidth, activities, loading]);

  useEffect(() => {
    setTableStyles();
  }, [setTableStyles, relatedObjects, savedColumns, activities, loading]);

  return (
    <PageSizing>
      <ContentWidth>
        <Toolbar>
          <TBSearchInput
            pill
            type={Input.INPUT_TYPES.SEARCH}
            placeholder={t('Find Activities')}
            value={searchTerm}
            onChange={setSearchTerm}
          />
          {activityCount ? (
            <KizenTypography as="h2" type="header">
              {activityCount}{' '}
              {activityCount === 1 ? title.singular : title.plural}
            </KizenTypography>
          ) : null}
          <div />
        </Toolbar>
      </ContentWidth>
      <ContentWidth ref={wrapperRef} className={'wrapper'} loading={loading}>
        <Loader loading={loading || !activities}>
          <StyledBigTableLayout
            hideCurtainWrapper={true}
            pagination={
              activityCount ? (
                <PaginationWrapper>
                  <TablePagination
                    page={page}
                    perPage={pageSize}
                    totalCount={activityCount}
                    onChangePage={(value) => setPage(value)}
                    onChangePerPage={(value) => {
                      setPage(1);
                      updatePageConfig({
                        page_size: value,
                      });
                    }}
                  />
                </PaginationWrapper>
              ) : null
            }
          >
            <StyledLazyLoadTable
              data={activities}
              columns={columns}
              headData={headData}
              staleData={fetching}
              onScrollThreshold={() => {}}
              noLoaderPadding={true}
              loaderSize={DEFAULT_PAGE_SIZE}
              tableBodyRef={tableBodyRef}
              virtualScrollerRef={virtualScrollerRef}
              virtualized
              className={'big-table'}
              totalSize={totalSize}
            >
              {/* Just a spacer when there are no results */}
              {(!activities || !activities.length) && <TRow />}
              {activitiesWithHoles?.length
                ? desktopVirtualizer.getVirtualItems().map((virtualItem, i) => {
                    let item = activitiesWithHoles[virtualItem.index];
                    let isNotes = false;

                    if (!item) {
                      // If we're looking at an empty slot, it represents the "notes" row for the
                      // immediately previous activity
                      item = activitiesWithHoles[virtualItem.index - 1];
                      isNotes = true;
                    }

                    const { employee, role, ...rest } = item;
                    const activity = {
                      employee: employee,
                      role: role,
                      ...rest,
                    };

                    const virtualItemStyles = {
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: `${virtualItem.size}px`,
                      transform: `translateY(${virtualItem.start}px)`,
                    };

                    const notesVisible = showNotesId === activity.id;

                    if (isNotes) {
                      return (
                        <CollapsibleRow
                          className="collapsible"
                          totalCols={columns.length}
                          in={notesVisible}
                          hideScrollbar
                          key={`${activity.id}-${virtualItem.key}-notes`}
                          style={virtualItemStyles}
                          endAdornment={
                            <td
                              width={80}
                              style={{
                                height: `${virtualItem.size}px`,
                                position: 'sticky',
                                right: 0,
                              }}
                            >
                              <div>
                                <GradientRightRow fadeIn />
                              </div>
                            </td>
                          }
                        >
                          {notesVisible ? (
                            <NotesContext.Provider value={notesValue}>
                              <NotesPanel
                                index={i}
                                activity={activity}
                                contactAccess={contactAccess}
                                updateNoteLoading={updateActivityLoading}
                                scheduledActivityQueryKey={queryKey}
                                onNotesChange={() => {}}
                                onNoteSaved={() =>
                                  queryClient.invalidateQueries({
                                    queryKey: invalidateQueryKey,
                                  })
                                }
                                setCurrentNote={setCurrentNote}
                                // The notes WYSIWYG editor isn't designed to have the 'value' change externally,
                                // so to get around this we force it to re-render when the editor key is explicitly changed
                                key={editorKey}
                                containerWidth={observedWidth}
                              />
                            </NotesContext.Provider>
                          ) : null}
                        </CollapsibleRow>
                      );
                    }
                    return (
                      <TRow
                        key={`${activity.id}-${virtualItem.key}-data`}
                        className={`virtual ${notesVisible ? 'selected' : ''}`}
                        columns={columns}
                        data={activity}
                        style={virtualItemStyles}
                      />
                    );
                  })
                : ''}
            </StyledLazyLoadTable>
          </StyledBigTableLayout>
        </Loader>
        <ConfirmDeletionModal
          show={showDeletionModal}
          onConfirm={deleteActivity}
          onHide={() => setShowDeletionModal(false)}
        >
          {t('This will permanently delete the scheduled activity.')}
        </ConfirmDeletionModal>
        {completeModalProps.show ? (
          <CompleteActivityModal
            {...completeActivity}
            {...completeModalProps}
            disabled={loggingActivity}
          />
        ) : null}
        {addModalProps.show && (
          <ScheduleActivityModal
            activity={editActivity}
            {...addModalProps}
            disabled={isSchedulingActivity}
          />
        )}
        <ConfirmationModal
          show={isConfirmActivityModalOpen}
          onConfirm={handleSubmitContactData}
          onHide={hideConfirmActivityModal}
          additionalButtonText={t('Discard Changes')}
          additionalButtonColor="red"
          onAdditionalConfirm={handleDiscardChanges}
          actionBtnColor="green"
          buttonText={t('Save')}
        >
          {t(
            'The record has unsaved changes. Would you like to save them or discard them before logging your activity?'
          )}
        </ConfirmationModal>
        {isConfirmModalOpen &&
          confirmationPropsRef.current.type === CONFIRMATION_TYPES.SAVE && (
            <ConfirmNoteEdits
              onCancel={hideConfirmationModal}
              onDiscard={() => {
                setCurrentNote(notesValue.originalNote);
                handleCancelNoteEdit();
                confirmationPropsRef.current?.onContinue?.(
                  notesValue.originalNote
                );
                confirmationPropsRef.current = {};
              }}
              onSave={() => {
                hideConfirmationModal();
                handleUpdateNote();
                confirmationPropsRef.current?.onContinue?.(currentNote);
                confirmationPropsRef.current = {};
              }}
            />
          )}
        {isConfirmModalOpen &&
          confirmationPropsRef.current.type ===
            CONFIRMATION_TYPES.SAVE_NOTIFY && (
            <ConfirmNoteEditsWithMentions
              onCancel={hideConfirmationModal}
              onDiscard={() => {
                setCurrentNote(notesValue.originalNote);
                handleCancelNoteEdit();
                confirmationPropsRef.current?.onContinue?.(
                  notesValue.originalNote
                );
                confirmationPropsRef.current = {};
              }}
              onSave={() => {
                hideConfirmationModal();
                handleUpdateNote();
                confirmationPropsRef.current?.onContinue?.(currentNote);
                confirmationPropsRef.current = {};
              }}
              onSaveAndNotify={() => {
                hideConfirmationModal();
                handleUpdateNote({ notifyMentioned: true });
                confirmationPropsRef.current?.onContinue?.(currentNote);
                confirmationPropsRef.current = {};
              }}
            />
          )}
        <EditColumnsModal
          fields={fields}
          isLoadingFields={isLoadingFields}
          isEditColumnsModalOpen={isEditColumnsModalOpen}
          hideEditColumnsModal={hideEditColumnsModal}
          columnSettings={columnSettings}
          isColumnSettingsLoading={isPageConfigLoading}
          defaultColumnSettings={defaultColumnSettings}
          updateColumnsConfig={updateColumnsConfig}
          showGroupedColumnEditor={showGroupedColumnEditor}
          columnWidths={columnWidths}
        />
      </ContentWidth>
    </PageSizing>
  );
};
